import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setAuthCallbacks } from "./api";

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
  role: string;
  networkId: number;
}

export interface NetworkInfo {
  networkId: number;
  networkName: string;
  userId: number;
  role: string;
  userType: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  networks: NetworkInfo[];
  login: (token: string, refreshToken: string, networks?: NetworkInfo[]) => void;
  logout: () => void;
  switchNetwork: (networkId: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function parseJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("auth_token"));
  const [networks, setNetworks] = useState<NetworkInfo[]>(() => {
    try {
      const stored = localStorage.getItem("auth_networks");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const queryClient = useQueryClient();
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const logout = useCallback(() => {
    const rt = localStorage.getItem("auth_refresh_token");
    if (rt) {
      api.post("/v1/auth/logout", { refreshToken: rt }).catch(() => {});
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_refresh_token");
    localStorage.removeItem("auth_networks");
    setToken(null);
    setNetworks([]);
    queryClient.clear();
  }, [queryClient]);

  // Wire up API client callbacks so 401 retries and failures flow back here
  useEffect(() => {
    setAuthCallbacks(
      (newToken) => setToken(newToken),
      () => logout(),
    );
  }, [logout]);

  // Proactive token refresh — check every 2 minutes, refresh if < 5 min left
  useEffect(() => {
    if (refreshTimer.current) clearInterval(refreshTimer.current);
    if (!token) return;

    const check = async () => {
      const currentToken = localStorage.getItem("auth_token");
      const rt = localStorage.getItem("auth_refresh_token");
      if (!currentToken || !rt) return;

      const exp = parseJwtExp(currentToken);
      if (!exp) return;

      const msLeft = exp - Date.now();
      if (msLeft < 5 * 60 * 1000) {
        try {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL || ""}/v1/auth/refresh`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ refreshToken: rt }),
            },
          );
          if (res.ok) {
            const data = await res.json();
            localStorage.setItem("auth_token", data.token);
            localStorage.setItem("auth_refresh_token", data.refreshToken);
            setToken(data.token);
          } else {
            logout();
          }
        } catch {
          // Network error — don't logout, retry next interval
        }
      }
    };

    refreshTimer.current = setInterval(check, 2 * 60 * 1000);
    // Also check immediately on mount
    check();

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [token, logout]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const res = await api.get("/v1/auth/me");
      if (!res.ok) throw new Error("Unauthorized");
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  useQuery({
    queryKey: ["auth", "networks"],
    queryFn: async () => {
      const res = await api.get("/v1/auth/networks");
      if (!res.ok) return { networks: [] };
      const data = await res.json();
      setNetworks(data.networks || []);
      localStorage.setItem("auth_networks", JSON.stringify(data.networks || []));
      return data;
    },
    enabled: !!token && networks.length === 0,
    retry: false,
  });

  useEffect(() => {
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_refresh_token");
      localStorage.removeItem("auth_networks");
    }
  }, [token]);

  useEffect(() => {
    if (error) logout();
  }, [error, logout]);

  const login = useCallback((newToken: string, refreshToken: string, newNetworks?: NetworkInfo[]) => {
    localStorage.setItem("auth_token", newToken);
    localStorage.setItem("auth_refresh_token", refreshToken);
    setToken(newToken);
    if (newNetworks) {
      setNetworks(newNetworks);
      localStorage.setItem("auth_networks", JSON.stringify(newNetworks));
    }
    queryClient.invalidateQueries({ queryKey: ["auth"] });
  }, [queryClient]);

  const switchNetwork = useCallback(async (networkId: number) => {
    const res = await api.post("/v1/auth/switch-network", { networkId });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || "Failed to switch network");
    }
    const data = await res.json();
    localStorage.setItem("auth_token", data.token);
    setToken(data.token);
    queryClient.invalidateQueries({ queryKey: ["auth"] });
    queryClient.clear();
    window.location.href = "/";
  }, [queryClient]);

  const user = data?.user ?? null;

  return (
    <AuthContext.Provider value={{ user, token, isLoading: !!token && isLoading, networks, login, logout, switchNetwork }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
